import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages — Email Platform Only
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import EmailCleanerPage from './pages/EmailCleanerPage';
import GlobalMailPage from './pages/GlobalMailPage';
import TemplateBuilderPage from './pages/TemplateBuilderPage';
import SenderDomainsPage from './pages/SenderDomainsPage';
import DomainVerificationPage from './pages/DomainVerificationPage';
import DomainPerformancePage from './pages/DomainPerformancePage';
import ProductionMonitorPage from './pages/ProductionMonitorPage';
import EnterpriseDashboardPage from './pages/EnterpriseDashboardPage';
import OpsCenterPage from './pages/OpsCenterPage';
import AdminPanelPage from './pages/AdminPanelPage';
import EmailExportPage from './pages/EmailExportPage';
import SettingsPage from './pages/SettingsPage';
import CreateWorkspacePage from './pages/CreateWorkspacePage';

// Layout
import Layout from './components/Layout';

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-12 h-12 rounded-lg bg-primary/20" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-12 h-12 rounded-lg bg-primary/20" />
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      {/* Email Platform Routes */}
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/email-cleaner" element={<ProtectedRoute><EmailCleanerPage /></ProtectedRoute>} />
      <Route path="/global-mail" element={<ProtectedRoute><GlobalMailPage /></ProtectedRoute>} />
      <Route path="/template-builder" element={<ProtectedRoute><TemplateBuilderPage /></ProtectedRoute>} />
      <Route path="/sender-domains" element={<ProtectedRoute><SenderDomainsPage /></ProtectedRoute>} />
      <Route path="/domain-verification" element={<ProtectedRoute><DomainVerificationPage /></ProtectedRoute>} />
      <Route path="/domain-performance" element={<ProtectedRoute><DomainPerformancePage /></ProtectedRoute>} />
      <Route path="/production-monitor" element={<ProtectedRoute><ProductionMonitorPage /></ProtectedRoute>} />
      <Route path="/enterprise" element={<ProtectedRoute><EnterpriseDashboardPage /></ProtectedRoute>} />
      <Route path="/ops-center" element={<ProtectedRoute><OpsCenterPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanelPage /></ProtectedRoute>} />
      <Route path="/admin/email-exports" element={<ProtectedRoute><EmailExportPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/workspaces/new" element={<ProtectedRoute><CreateWorkspacePage /></ProtectedRoute>} />

      {/* Catch all → dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            style: {
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              color: 'hsl(var(--foreground))',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
